import { IQueryResult } from "WorkItemTracking/Scripts/OM/QueryInterfaces";
import { QueryResultsProvider } from "WorkItemTracking/Scripts/Controls/WorkItemsProvider";
import * as WITOM from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import * as Resources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import { IQueriesHubContext } from "WorkItemTracking/Scripts/Queries/Components/QueriesHubContext";
import * as Dialogs from "VSS/Controls/Dialogs";
import { PageSizes } from "WorkItemTracking/Scripts/OM/WorkItemConstants";
import * as Utils_String from "VSS/Utils/String";
import * as WITConstants from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { QueryResultGrid } from "WorkItemTracking/Scripts/Controls/Query/QueryResultGrid";
import { publishEvent, TelemetryEventData } from "VSS/Telemetry/Services";
import { isImportWorkItemsProvider } from "WorkItemTracking/Scripts/Controls/ImportWorkItemsProvider";

export interface IIndentationsData {
    maxIndentation: number;
    indentationsMap: { [id: number]: number };
}

export class QueryResultCSVExporter {
    private static QueryResultLimit: number = 20000;
    private c_newlineChar = "\n";
    private c_commaDelimiter = ",";
    private static readonly CSVFileName: string = "data.csv";
    // 'Import' in the name to allow grouping the CI events for both the export and the upcoming import functionality
    private static readonly TelemetryArea: string = "WebAccessQueryCSVImportExport";

    constructor(
        private _grid: QueryResultGrid,
        private _store: WITOM.WorkItemStore,
        private _workItemsProvider: QueryResultsProvider,
        private _queriesHubContext: IQueriesHubContext
    ) {}

    public downloadCSV(): void {
        this._publishTelemetry("ExportButtonClicked");
        if (!this._workItemsProvider || !this._store || !this._grid || !this._queriesHubContext) {
            return;
        }

        const fileName = this._workItemsProvider.queryDefinition
            ? this._workItemsProvider.queryDefinition.name + ".csv"
            : QueryResultCSVExporter.CSVFileName;

        let queryResult = this._workItemsProvider.queryResultsModel;
        if (!queryResult && isImportWorkItemsProvider(this._workItemsProvider)) {
            // if it's importWorkItems provider and it hasn't imported yet, use empty dataset
            queryResult = {} as IQueryResult;
        }

        this._getCSVContent(queryResult)
            .then(data => {
                if (data && data.length > 0) {
                    const blob = new Blob(["\ufeff" + data], { type: "text/csv;charset=utf-8;" });
                    if ((window.navigator as any).msSaveOrOpenBlob) {
                        (window.navigator as any).msSaveBlob(blob, QueryResultCSVExporter.CSVFileName); // IE11 support
                    } else {
                        const a = window.document.createElement("a");
                        const blobUrl = window.URL.createObjectURL(blob);
                        a.href = blobUrl;
                        a.download = fileName;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(blobUrl); // Dispose blob
                    }
                    this._publishTelemetry("ExportSuccessful");
                } else {
                    Dialogs.MessageDialog.showMessageDialog(Resources.ExportToCsvNoQueryResultsMessage, {
                        title: Resources.ExportToCSV,
                        buttons: [Dialogs.MessageDialog.buttons.ok]
                    });
                }
            })
            .catch(err => {
                if (err) {
                    this._grid._statusUpdate(err);
                }
                this._publishTelemetry("ExportFailed", { err });
            });
    }

    private _getCSVContent(queryResult: IQueryResult): Promise<string> {
        return new Promise((resolve, reject) => {
            let isCSVExporting = true;

            const successCallback = queryResultsModel => {
                if (queryResultsModel && queryResultsModel.targetIds && queryResultsModel.targetIds.length > 0) {
                    this._queriesHubContext.triageViewActionCreator.onCSVExportStatusChanged(isCSVExporting);

                    if (queryResultsModel.targetIds.length <= QueryResultCSVExporter.QueryResultLimit) {
                        this._getCSVContentHelper(queryResultsModel)
                            .then(csv => {
                                resolve(csv);
                            })
                            .catch(err => {
                                reject(err);
                            });
                    } else {
                        reject(Utils_String.format(Resources.ExportToCsvQueryResultTooBig, QueryResultCSVExporter.QueryResultLimit));
                    }
                    // Reset the 'Export' button state irrespective of success or failure of the export operation at the end
                    isCSVExporting = false;
                    this._queriesHubContext.triageViewActionCreator.onCSVExportStatusChanged(isCSVExporting);
                } else {
                    resolve(""); // Query results did not return any work items
                }
            };

            // Use what's cached on the client if possible because it reflects the results present in the UI
            if (queryResult) {
                successCallback(queryResult);
            } else {
                this._workItemsProvider._beginQuery(successCallback, reject);
            }
        });
    }

    private async _getCSVContentHelper(queryResultsModel: IQueryResult): Promise<string> {
        const isTreeQuery: boolean = queryResultsModel.isTreeQuery;
        const isLinkQuery: boolean = queryResultsModel.isLinkQuery;

        this._publishTelemetry("ExportQueryType", { isTreeQuery, isLinkQuery });
        // Id always 1st column
        const mandatoryRefNames = [
            WITConstants.CoreFieldRefNames.Id,
            WITConstants.CoreFieldRefNames.Title,
            WITConstants.CoreFieldRefNames.WorkItemType,
            WITConstants.CoreFieldRefNames.State
        ];
        const mandatoryColNames = mandatoryRefNames.map(refName => this._store.getFieldDefinition(refName).name);
        const refNames = this._insertToFrontIfNotFound(queryResultsModel.columns.map(col => col.name), mandatoryRefNames);
        const colNames = this._insertToFrontIfNotFound(queryResultsModel.columns.map(col => col.text), mandatoryColNames);
        const idIndex = refNames.indexOf(WITConstants.CoreFieldRefNames.Id);

        const targetIds = this._deduplicate(queryResultsModel.targetIds);
        const sourceIds = isTreeQuery ? queryResultsModel.sourceIds : []; // Link Query therefore returned as flat list

        const promises = [];

        // Populate promises array with paged rows of escaped content
        for (let i = 0; i < targetIds.length; i += PageSizes.QUERY) {
            let subsetTargetIds = targetIds.slice(i, i + PageSizes.QUERY);
            subsetTargetIds = subsetTargetIds.filter(id => id > 0); // export only saved work items
            promises.push(this._getPagedCSVRows(subsetTargetIds, refNames, idIndex));
        }

        // If query type is direct links/1-hop, we warn the user that it'll be flattened
        if (isLinkQuery && !isTreeQuery) {
            const alertText = Utils_String.format(Resources.DirectLinksFlattenedMessage);
            Dialogs.MessageDialog.showMessageDialog(alertText, {
                title: Resources.WarningExportingDirectLinksTitle,
                buttons: [Dialogs.MessageDialog.buttons.ok]
            });
        }

        const csvRowsArr = await Promise.all(promises);

        csvRowsArr.unshift([colNames]);
        const indexToSplitOn = refNames.indexOf(WITConstants.CoreFieldRefNames.Title); // Must not be -1
        const indentationsData: IIndentationsData = this._getIndentationsData(targetIds, sourceIds);

        const csvTexts = this._getCSVTextsFromRows(csvRowsArr, idIndex, indexToSplitOn, indentationsData);

        // Return filtered, concatenated CSV string
        return Promise.resolve(csvTexts.filter(csvText => csvText.length > 0).join(this.c_newlineChar));
    }

    private _deduplicate(arr: number[]): number[] {
        const result = [];
        const hasVisitedDict = {};

        arr.forEach(elem => {
            if (!hasVisitedDict[elem]) {
                hasVisitedDict[elem] = true;
                result.push(elem);
            }
        });

        return result;
    }

    // Case Sensitive
    private _insertToFrontIfNotFound(arr: string[], targets: string[]): string[] {
        const targetsMap = {};
        targets.forEach(target => {
            targetsMap[target] = true;
        });

        arr.forEach(elem => {
            if (targetsMap[elem]) {
                delete targetsMap[elem];
            }
        });

        return targets.filter(target => targetsMap[target]).concat(arr);
    }

    // Construct the CSV string
    private _getCSVTextsFromRows(csvRowsArr: string[], idIndex: number, indexToSplitOn: number, indentationsData: IIndentationsData): string[] {
        let foundError = false;

        const maxIndentation = indentationsData.maxIndentation;
        const indentationsMap = indentationsData.indentationsMap;

        const csvTexts = csvRowsArr.map((rows, index) => {
            if (rows.length === 0) {
                foundError = true;
                return "";
            }

            let csvText = "";

            // Form the CSV string while reconciling the different indentations with the maximum one
            for (let i = 0; i < rows.length; i++) {
                const isColHeaderRow = index == 0 && i == 0;
                const id = Number(rows[i][idIndex].replace(/"/g, ""));
                const indentation = indentationsMap[id];

                // Append 'prefix'
                for (let j = 0; j < indexToSplitOn; j++) {
                    csvText += rows[i][j] + this.c_commaDelimiter;
                }

                // Handle indentation
                for (let j = 0; j <= maxIndentation; j++) {
                    if (isColHeaderRow) {
                        csvText += rows[i][indexToSplitOn] + (maxIndentation > 0 ? " " + String(j + 1) : ""); // ie. Title 1, Title 2, ...
                    } else if (j === indentation) {
                        csvText += rows[i][indexToSplitOn]; // Place field value in correct space
                    }

                    csvText += j < maxIndentation || indexToSplitOn < rows[i].length - 1 ? this.c_commaDelimiter : "";
                }

                // Append 'suffix'
                for (let j = indexToSplitOn + 1; j < rows[i].length; j++) {
                    csvText += rows[i][j] + (j < rows[i].length - 1 ? this.c_commaDelimiter : "");
                }

                csvText += i < rows.length - 1 ? this.c_newlineChar : "";
            }

            return csvText;
        });

        // Show error status for incomplete results
        if (foundError) {
            this._grid._statusUpdate(Resources.ExportToCSVError);
        }

        return csvTexts;
    }

    private _getPagedCSVRows(targetIds: number[], refNames: string[], idIndex: number): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const errorCallback = error => {
                resolve([]);
            };

            this._store.beginPageWorkItems(
                targetIds,
                refNames,
                pagingData => {
                    const indicesMap = this._getIndicesMap(targetIds);

                    // Rearrange rows back to original order
                    const newRows = new Array(targetIds.length);
                    pagingData.rows.forEach((row, index) => {
                        const id = row[idIndex];
                        const origIndex = indicesMap[id];
                        newRows[origIndex] = pagingData.rows[index];
                    });

                    resolve(this._getEscapedCSVRows(newRows));
                },
                errorCallback,
                null
            );
        });
    }

    // Create dictionary for original order lookup - assumes that targetIds has no duplicates
    private _getIndicesMap(targetIds: number[]): {} {
        const result = {};

        targetIds.forEach((targetId, index) => {
            result[targetId] = index;
        });

        return result;
    }

    // Creates dictionary for indentation level - assumes that targetIds has no duplicates
    private _getIndentationsData(targetIds: number[], sourceIds: number[]): IIndentationsData {
        const indentationsData: IIndentationsData = {
            maxIndentation: 0,
            indentationsMap: {}
        };

        targetIds.forEach((targetId, index) => {
            let indentation = 0;
            if (sourceIds.length > 0 && sourceIds[index] !== 0) {
                indentation = indentationsData.indentationsMap[sourceIds[index]] + 1; // Must exist due to tree structure
            }
            indentationsData.indentationsMap[targetId] = indentation;
            indentationsData.maxIndentation = Math.max(indentationsData.maxIndentation, indentation);
        });

        return indentationsData;
    }

    private _getEscapedCSVRows(rows: any[]): string[] {
        if (!rows || rows.length === 0) {
            return [];
        }

        const result = [];

        for (let i = 0; i < rows.length; i++) {
            if (!(rows[i] instanceof Array)) {
                continue; // Could not find work item due to invalid WI ID.
            }

            const escapedRow = new Array(rows[i].length);
            for (let j = 0; j < rows[i].length; j++) {
                escapedRow[j] = this._escapeForCSV(rows[i][j]);
            }

            result.push(escapedRow);
        }

        return result;
    }

    private _escapeForCSV(obj: any): string {
        let result = "";

        // Do we want to convert booleans to 1/0?
        if (typeof obj === typeof true) {
            result = obj ? "1" : "0";
        } else {
            // If obj is a datetime string, use local time and use "d/m/yyyy hh:mm:ss AM/PM" format (for example: 1/7/2020 11:33:29 AM)
            if (obj instanceof Date) {
                const dateobj = obj as Date;

                // Not using toLocaleString() because it adds a comma between the date and string.
                // Excel will automatically identify this date time format if there is no comma, making it easier for the user to modify the date formatting within Excel.
                obj = `${dateobj.toLocaleDateString()} ${dateobj.toLocaleTimeString()}`;
            }
            const str = obj == null ? "" : String(obj).replace(/"/g, '""');
            if (str !== "") {
                result += `\"${str}\"`;
            }
        }

        return result;
    }

    private _publishTelemetry(featureName: string, properties: { [key: string]: string | number | boolean } = {}): void {
        publishEvent(new TelemetryEventData(QueryResultCSVExporter.TelemetryArea, featureName, properties));
    }
}
